import React from "react";
import { Draggable, DraggableProvided, DraggableStateSnapshot } from "react-beautiful-dnd";
import styled from "styled-components";

import { FlowStepData } from "../../../rule-engine-core/Flow/Steps/FlowStepData";
import { FlowStepSequence } from "../../../rule-engine-core/Flow/Structure/FlowStepSequence";

import {
    FlowStepOutputInstructions,
    FlowStepOutputInstructionType,
} from "../../../rule-engine-core/Flow/Structure/FlowStepOutputInstructions";

import { FlowEditContext } from "../../FlowEditContext";
import { ObserveMultiState } from "../../../CommonUI/StateManagement/ObserveMultiState";

import { RenderStepPath, StepConnectOtherPaths, StepConnectSection } from "./RenderStepPath";
import { RenderStepInstruction } from "./RenderStepInstruction";
import { StepDescriptionIcon } from "../../../CommonUI/Icon/StepDescriptionIcon";
import { ObserveState } from "../../../CommonUI/StateManagement/ObserveState";
import { FlowConstants } from "../../../rule-engine-core/Flow/Utilities/FlowConstants";

import { FlowUtilities } from "../../../rule-engine-core/Flow/Utilities/FlowUtilities";
import { BaseFlowStepEditor } from "../../Steps/BaseFlowStepEditor";
import { FlowStepSequenceType } from "../../../rule-engine-core/Flow/Structure/FlowStepSequenceType";
import { Box } from "@mui/material";
import { UIStyleConstants } from "../../../rule-engine-core/Common/UIStyleConstants";
import { UILabel } from "../../../CommonUI/BuildlingBlocks/UILabel";
import { BaseTestResultData } from "../../../rule-engine-core/Common/Testing/BaseTestResultData";
import { TestContext } from "../../../CommonUI/Testing/TestContext";

interface IRegularStepHost {
    sequence: FlowStepSequence;
    editContext: FlowEditContext;
    index: number;
    step: FlowStepData;
}

export class RenderStepHost extends React.Component<
    IRegularStepHost,
    { isDragging: boolean; isCollapsed: boolean; isDroppingOver: boolean }
> {
    element: HTMLDivElement | null = null;

    componentDidUpdate() {
        if (
            this.props.editContext.draggingControl &&
            this.props.editContext.draggingControl.source.current === this.props.step._id &&
            this.element
        ) {
            const el = this.element;
            const targetWidth: number = FlowConstants.DRAGGING_STEP_WIDTH;
            const halfWidth: number = targetWidth / 2;
            const targetHeight: number = FlowConstants.DRAGGING_STEP_HEIGHT;
            const halfHeight: number = targetHeight / 2;

            el.style.width = `${targetWidth}px`;
            el.style.height = `${targetHeight}px`;
            el.style.left = `${this.props.editContext.draggingControl.pos.x - halfWidth}px`;
            el.style.top = `${this.props.editContext.draggingControl.pos.y - halfHeight}px`;
        }
    }

    isShowStepConnection(step: FlowStepData): boolean {
        let sequence = step.sequence;
        if (!sequence) return false;
        let index = sequence.steps.indexOf(step);
        let isLast = sequence.steps.length <= index + 1;

        let otherPaths: FlowStepOutputInstructions[] = step.getOutcomeInstructions();

        return !isLast || otherPaths.length !== 0;
    }

    render() {
        let { step, index } = this.props;
        let nextPathTitle: string | null = null;
        let nextPathTooltipTitle: string | null = null;
        let otherPaths: FlowStepOutputInstructions[] = step.getOutcomeInstructions();
        let editContext = this.props.editContext;

        let stepui = editContext.getOrCreateStepEditor(step.__type!, step);

        if (otherPaths.length !== 0) {
            // removes first item
            let defaultPath: FlowStepOutputInstructions | null = otherPaths.splice(0, 1)[0];
            let continuePaths = otherPaths.filter((ins) => ins.strategy === FlowStepOutputInstructionType.CONTINUE);

            nextPathTooltipTitle = [defaultPath, ...continuePaths].map((ins) => ins.pathName).join(" | ");
            nextPathTitle = continuePaths.length > 0 ? `${continuePaths.length + 1} Paths` : nextPathTooltipTitle;
        }

        return (
            <Draggable
                key={step._id}
                draggableId={step._id}
                index={index}
            >
                {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
                    return (
                        <div
                            onContextMenu={(e: React.MouseEvent<HTMLDivElement>) => {
                                e.preventDefault();
                                e.stopPropagation();
                                this.props.editContext.contextControl = {
                                    selectedId: step._id,
                                    mouseX: e.clientX,
                                    mouseY: e.clientY,
                                };
                            }}
                            ref={(node: HTMLDivElement) => {
                                provided.innerRef(node);
                                this.element = node;
                            }}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                                ...(provided.draggableProps.style || {}),
                                cursor: snapshot.isDragging ? "move" : "default",
                            }}
                        >
                            {!snapshot.isDragging &&
                                this.props.sequence.sequenceType !== FlowStepSequenceType.BRANCH_MERGE &&
                                this.RenderNonBranchStep(
                                    stepui,
                                    editContext,
                                    step,
                                    nextPathTitle,
                                    nextPathTooltipTitle,
                                    otherPaths,
                                )}

                            {!snapshot.isDragging &&
                                this.props.sequence.sequenceType === FlowStepSequenceType.BRANCH_MERGE &&
                                this.RenderBranchStep(
                                    stepui,
                                    editContext,
                                    step,
                                    nextPathTitle,
                                    nextPathTooltipTitle,
                                    otherPaths,
                                )}

                            {snapshot.isDragging && (
                                <Box
                                    padding={"5px"}
                                    width={"200px"}
                                    bgcolor={UIStyleConstants.BLUE_VERY_LIGHT_COLOR}
                                >
                                    <UILabel>{step.name || ""}</UILabel>
                                </Box>
                            )}
                        </div>
                    );
                }}
            </Draggable>
        );
    }

    private RenderNonBranchStep(
        stepui: BaseFlowStepEditor,
        editContext: FlowEditContext,
        step: FlowStepData,
        nextPathTitle: string | null,
        nextPathTooltipTitle: string | null,
        otherPaths: FlowStepOutputInstructions[],
    ) {
        let isSelected = editContext.selection === step._id;
        let isInvalid = stepui.getFlowStepData().getValidationIssues().length > 0;
        let borderColor = "transparent";
        if (isInvalid) borderColor = FlowConstants.ERROR_STEP_COLOR;
        if (isSelected) borderColor = FlowConstants.SELECTED_STEP_COLOR;

        return (
            <>
                <TestContext.Consumer>
                    {(testcontext: BaseTestResultData | undefined) => {
                        const __ret = this.getStepColors(testcontext, step, borderColor);
                        let backgroundColor = __ret.backgroundColor;
                        borderColor = __ret.borderColor;

                        return (
                            <Box
                                data-testid={`step_${step._id}`}
                                border={!FlowUtilities.IsEndStep(stepui.getFlowStepData()) ? "2px solid" : "0px"}
                                borderColor={borderColor}
                                borderRadius="4pxpx"
                                padding={"0px"}
                                fontSize={"12px"}
                                lineHeight={"24px"}
                                bgcolor={backgroundColor}
                                color={UIStyleConstants.GRAY_DARK_COLOR}
                                display={"flex"}
                                flexDirection={"column"}
                                position={"relative"}
                                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                    event.stopPropagation();
                                    editContext.setSelection(stepui.getFlowStepData());
                                }}
                            >
                                {stepui.icon && <StepIconDiv>{stepui.icon}</StepIconDiv>}
                                {step.description !== "" && (
                                    <StepDescriptionIconDiv>
                                        <StepDescriptionIcon />
                                    </StepDescriptionIconDiv>
                                )}
                                {stepui.render()}

                                {otherPaths.length !== 0 && (
                                    <StepConnectOtherPaths>
                                        {otherPaths.map((stepOutput: FlowStepOutputInstructions) => {
                                            return (
                                                <ObserveMultiState
                                                    listenTo={[stepOutput]}
                                                    key={"path" + stepOutput.pathName}
                                                    control={() => {
                                                        return (
                                                            <StepConnector
                                                                id={
                                                                    stepOutput.strategy ===
                                                                    FlowStepOutputInstructionType.BRANCH
                                                                        ? stepOutput.getElementId()
                                                                        : undefined
                                                                }
                                                            />
                                                        );
                                                    }}
                                                />
                                            );
                                        })}
                                    </StepConnectOtherPaths>
                                )}
                            </Box>
                        );
                    }}
                </TestContext.Consumer>
                <StepConnectSection>
                    {this.isShowStepConnection(step) && (
                        <RenderStepPath
                            step={step}
                            title={nextPathTitle ? nextPathTitle : undefined}
                            tooltipTitle={nextPathTooltipTitle ? nextPathTooltipTitle : undefined}
                            renderUI={
                                nextPathTitle ? () => this.renderStepSummary(editContext, step, otherPaths) : undefined
                            }
                            onTitleClick={(elem: HTMLElement) => {
                                editContext.setSelectedStepStrategy(elem, step);
                            }}
                        />
                    )}
                </StepConnectSection>
            </>
        );
    }

    private getStepColors(testcontext: BaseTestResultData | undefined, step: FlowStepData, borderColor: string) {
        let myContext = testcontext ? testcontext.findItem(step._id) : undefined;
        let backgroundColor = UIStyleConstants.WHITE_COLOR;

        if (testcontext) {
            if (myContext) {
                if (myContext.isError) {
                    backgroundColor = UIStyleConstants.RED_VERY_LIGHT_COLOR;
                    borderColor = UIStyleConstants.RED_COLOR;
                } else {
                    backgroundColor = UIStyleConstants.GREEN_VERY_LIGHT_COLOR;
                    borderColor = UIStyleConstants.GREEN_COLOR;
                }
            } else {
                borderColor = "transparent";
            }
        }
        return { backgroundColor, borderColor };
    }

    private RenderBranchStep(
        step: BaseFlowStepEditor,
        editContext: FlowEditContext,
        stepData: FlowStepData,
        _nextPathTitle: string | null,
        _nextPathTooltipTitle: string | null,
        _otherPaths: FlowStepOutputInstructions[],
    ) {
        let isSelected = editContext.selection === step._id;
        let isInvalid = stepData.getValidationIssues().length > 0;
        let borderColor = "transparent";
        if (isInvalid) borderColor = FlowConstants.ERROR_STEP_COLOR;
        if (isSelected) borderColor = FlowConstants.SELECTED_STEP_COLOR;
        return (
            <TestContext.Consumer>
                {(testcontext: BaseTestResultData | undefined) => {
                    const __ret = this.getStepColors(testcontext, stepData, borderColor);
                    let backgroundColor = __ret.backgroundColor;
                    borderColor = __ret.borderColor;

                    return (
                        <Box
                            display={"flex"}
                            minHeight={"57px"}
                        >
                            <Box
                                minWidth={"150px"}
                                display={"flex"}
                                flexDirection={"column"}
                                data-testid={`step_${step._id}`}
                                // selected={editContext.selection === step._id}
                                // invalid={step.getFlowStepData().getValidationIssues().length > 0}
                                onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                                    event.stopPropagation();
                                    editContext.setSelection(step.getFlowStepData());
                                }}
                                //                hidden={FlowUtilities.IsEndStep(step.getFlowStepData())}
                                border={"2px"}
                                borderColor={borderColor}
                                borderRadius="4pxpx"
                                style={{ borderStyle: "solid" }}
                                bgcolor={backgroundColor}
                            >
                                {step.icon && <StepIconDiv>{step.icon}</StepIconDiv>}
                                {step.description !== "" && (
                                    <StepDescriptionIconDiv>
                                        <StepDescriptionIcon />
                                    </StepDescriptionIconDiv>
                                )}
                                {step.render()}
                            </Box>
                            <Box
                                display={"flex"}
                                flexDirection={"column"}
                                width={"10px"}
                                height={"100%"}
                                alignSelf={"center"}
                            >
                                <Box
                                    height={"1px"}
                                    bgcolor={UIStyleConstants.GRAY_LIGHT_COLOR}
                                ></Box>
                            </Box>
                        </Box>
                    );
                }}
            </TestContext.Consumer>
        );
    }

    private renderStepSummary(
        editContext: FlowEditContext,
        step: FlowStepData,
        otherPaths: FlowStepOutputInstructions[],
    ) {
        let paths = otherPaths.filter((ins) => ins.strategy !== FlowStepOutputInstructionType.BRANCH);

        return (
            <StepConnectSummaryDiv>
                {paths.map((ins, idx) => (
                    <ObserveState
                        listenTo={ins}
                        key={idx}
                        control={() => (
                            <RenderStepInstruction
                                editContext={editContext}
                                step={step}
                                instruction={ins}
                                key={idx}
                            />
                        )}
                    />
                ))}
            </StepConnectSummaryDiv>
        );
    }
}

export const StepConnector = styled.div`
    min-width: 30px;
    min-height: 10px;
    position: relative;
`;

export const StepConnectSummaryDiv = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-around;
    margin-left: auto;
    padding: 6px 0;
    position: relative;
`;

export const StepIconDiv = styled.div`
    position: absolute;
    top: 3px;
    right: 4px;
    width: 12px;
    opacity: 0.3;
    display: flex;

    img {
        width: 100%;
    }
`;

export const StepDescriptionIconDiv = styled.div`
    position: absolute;
    bottom: 4px;
    right: 4px;
    display: flex;
`;
